import React, { useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { api } from '../../services/api';

// FR-7.2: threshold comes from GET /prompts/quality-config so an env-var change on the
// Container App takes effect on next page load without a frontend redeploy. Cached at
// module level so a grid of cards triggers a single fetch per page load.
const DEFAULT_THRESHOLD = 7;
let thresholdPromise: Promise<number> | null = null;

export function fetchAutoApproveThreshold(): Promise<number> {
  if (!thresholdPromise) {
    thresholdPromise = api
      .getQualityConfig()
      .then(cfg => cfg.autoApproveThreshold)
      .catch(() => {
        thresholdPromise = null; // allow retry on next mount
        return DEFAULT_THRESHOLD;
      });
  }
  return thresholdPromise;
}

export function useAutoApproveThreshold(): number {
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  useEffect(() => {
    let cancelled = false;
    fetchAutoApproveThreshold().then(value => {
      if (!cancelled) setThreshold(value);
    });
    return () => { cancelled = true; };
  }, []);
  return threshold;
}

interface NeedsImprovementBadgeProps {
  qualityScore?: number | null;
  className?: string;
}

// Visual-only badge (FR-7.2): shown when a prompt's stored quality score is below the
// auto-approve threshold. Computed at read time — raising the threshold hides it on
// next load with no data migration.
export const NeedsImprovementBadge: React.FC<NeedsImprovementBadgeProps> = ({ qualityScore, className }) => {
  const threshold = useAutoApproveThreshold();
  if (qualityScore === null || qualityScore === undefined || qualityScore >= threshold) {
    return null;
  }
  return (
    <Badge
      className={`bg-amber-100 text-amber-800 border border-amber-300 text-[11px] font-medium ${className ?? ''}`}
      aria-label={`Needs improvement — quality score ${qualityScore} of 10`}
    >
      Needs Improvement
    </Badge>
  );
};
